import { useState, useMemo } from 'react';
import { Flex } from 'antd';
import { PageMeta } from '@app/components/meta/Meta';
import { PageLayout } from '@app/components/page';
import { PageHeader } from '@app/components/page/PageHeader';
import { PageIntro } from '@app/components/page/PageIntro';
import { PageTitle } from '@app/components/page/PageTitle';
import { StyledTable } from '@app/components/table/Table';
import useBillingReports from '@app/hooks/billingReports/useBillingReports';
import TotalsCard from './components/TotalsCard';
import FiltersCard from './components/FiltersCard';
import ExportBtn from './components/ExportBtn';
import { BASE_COLUMNS } from './utils/constants';
import { analyseBillingData, createBillingTableCols } from './utils/functions';
import { StyledAlert } from '@app/components/alert/Alert';

const BillingReportsView = () => {
    const [channelPartnersIds, setChannelPartnersIds] = useState<string[]>([]);
    const [dates, setDates] = useState<{ start: string; end: string }>({
        start: '',
        end: '',
    });
    const { data, isLoading } = useBillingReports(
        { ...dates, channel_id: channelPartnersIds },
        { staleTime: 1000 * 60 * 5 }
    );

    const billingData = Array.isArray(data) ? data : [];
    const { tableData, currencies, grandTotals } = analyseBillingData(billingData);

    const billingCols = useMemo(
        () => createBillingTableCols(BASE_COLUMNS, currencies),
        [currencies]
    );

    const updateDates = (dateRange: { start: string; end: string }) => {
        setDates(dateRange);
    };

    const updateChannelPartnersIds = (id: string, action: 'select' | 'deselect') => {
        if (id === 'reset') {
            setChannelPartnersIds([]);
        } else if (action === 'select') {
            setChannelPartnersIds((prev) => [...prev, id]);
        } else {
            setChannelPartnersIds((prev) => prev.filter((pid) => pid !== id));
        }
    };

    return (
        <>
            <PageMeta title="Billing" />
            <StyledAlert title="BETA" />
            <PageHeader>
                <Flex justify="space-between" gap={20}>
                    <div>
                        <PageTitle title="Billing Reports" />
                        <PageIntro>
                            <p>Review charges for services rendered</p>
                        </PageIntro>
                    </div>
                    <ExportBtn tableData={tableData} currencies={currencies} dates={dates} />
                </Flex>
            </PageHeader>
            <PageLayout className="billing-reports-layout">
                <PageLayout.Content>
                    <div className="summary-container">
                        <FiltersCard
                            updateDates={updateDates}
                            channelPartnersIds={channelPartnersIds}
                            updateChannelPartnersIds={updateChannelPartnersIds}
                        />
                        <TotalsCard totals={grandTotals} />
                    </div>
                    <StyledTable
                        columns={billingCols ?? []}
                        dataSource={tableData || []}
                        pagination={false}
                        loading={isLoading}
                    />
                </PageLayout.Content>
            </PageLayout>
        </>
    );
};

export default BillingReportsView;
